import { diagnoseImageLoadError } from '@glyphic/shared-core'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { type MouseEvent,useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { mergeReducedMotionPreset, useThemeMotion } from '../animation/themeMotion'
import { type ReferenceImageItem } from '../api/referenceImages'
import { resolveReferenceImageDisplayUrl } from '../api/referenceUpload'
import { useToast } from '../context/ToastContext'
import { useBrokenImageHttpStatus } from '../hooks/useBrokenImageHttpStatus'
import { type ImageInteractionPolicy,useImageInteractionPolicy } from '../hooks/useImageInteractionPolicy'
import { useTouchLongPress } from '../hooks/useLongPress'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { toastForBrowserImageDownload, triggerBrowserImageDownload } from '../lib/clientImageDownload'
import { copyImageToClipboard } from '../lib/copyImageToClipboard'
import { referenceImageThumbnailUrl } from '../lib/referenceImageUrl'
import { refreshReferenceUrlOnce, type RefreshUrlExecutor } from '../lib/referenceUrlRefresh'
import { normalizeImageLightboxItem } from '../lib/resolvedMediaImage'
import { ChromeMonoTag } from './ChromeTags'
import { HorizontalChipStrip } from './HorizontalChipStrip'
import {
  buildImageContextMenuItems,
  ImageContextDesktopMenu,
  ImageContextMobileSheet,
  ImageMoreActionsTrigger,
  useImageContextPayload,
} from './ImageContextActions'
import { ImageLoadFailurePlaceholder } from './ImageLoadFailurePlaceholder'

/** 仅限制缩小下限，放大不设上限 */
const MIN_ZOOM = 0.25

function clampZoomIn(next: number): number {
  return Math.max(MIN_ZOOM, next)
}

/** 滚轮缩放：delta 越大变化越快；高倍率下同滚幅度绝对增量更大（√zoom 加速） */
function applyWheelZoom(currentZoom: number, deltaY: number): number {
  const capped = Math.max(-200, Math.min(200, deltaY))
  if (capped === 0) return currentZoom
  const intensity = Math.abs(capped) / 100
  const boost = Math.sqrt(Math.max(currentZoom, MIN_ZOOM))
  const ratio =
    capped < 0
      ? 1 + 0.12 * intensity * boost
      : 1 / (1 + 0.12 * intensity * boost)
  return clampZoomIn(+(currentZoom * ratio).toFixed(4))
}

export type ImageLightboxItem = {
  src: string
  alt?: string
  prompt?: string
  subtitle?: string
  taskLabel?: string
  details?: Array<{ label: string; value: string }>
  referenceImageUrls?: string[]
  /** 图库对象键；有值时灯箱可对这张图即时重签（打开/失败时自动补一份新签名） */
  objectKey?: string
}

type Props = {
  open: boolean
  src?: string
  alt?: string
  items?: ImageLightboxItem[]
  index?: number
  onIndexChange?: (index: number) => void
  angular?: boolean
  /**
   * 按 objectKey 重新签发下载地址（通常就是 `refreshReferenceImageUrlByObjectKey` 的包一层）。
   * 有值时：打开/切换大图先要一份新鲜签名，加载失败也先补签重试，仍失败才报错。
   * 不传则保持旧行为（直接用传入的 src）。
   */
  onRefreshImageUrl?: (item: ImageLightboxItem) => Promise<ReferenceImageItem | null>
  onClose: () => void
}

function LightboxRatioTag({
  angular,
  zoom,
}: {
  angular: boolean
  zoom: number
}) {
  return <ChromeMonoTag angular={angular}>{`${Math.round(zoom * 100)}%`}</ChromeMonoTag>
}

function clampCompareOffset(next: { x: number; y: number }, z: number, containerEl: HTMLElement | null) {
  if (z <= 1) return { x: 0, y: 0 }
  if (!containerEl) {
    const max = Math.max(48, (z - 1) * 260)
    return {
      x: Math.max(-max, Math.min(max, next.x)),
      y: Math.max(-max, Math.min(max, next.y)),
    }
  }
  const img = containerEl.querySelector('img')
  if (!img) {
    const max = Math.max(48, (z - 1) * 260)
    return {
      x: Math.max(-max, Math.min(max, next.x)),
      y: Math.max(-max, Math.min(max, next.y)),
    }
  }
  const containerRect = containerEl.getBoundingClientRect()
  const imgRect = img.getBoundingClientRect()
  const scaledW = imgRect.width
  const scaledH = imgRect.height
  const maxOffsetX = Math.max(0, (scaledW - containerRect.width) / 2)
  const maxOffsetY = Math.max(0, (scaledH - containerRect.height) / 2)
  return {
    x: Math.max(-maxOffsetX, Math.min(maxOffsetX, next.x)),
    y: Math.max(-maxOffsetY, Math.min(maxOffsetY, next.y)),
  }
}

/** 对比单列：在各自区域内滚轮缩放、双指捏合、缩放后拖拽；互不影响另一侧 */
function CompareZoomablePane({
  src,
  alt = '',
  headerLabel,
  angular,
  isActive,
  onActivate,
  paneAriaLabel,
  onSaveRequest,
  onContextMenu,
  syntheticLongPress = false,
  useEmbeddedNativeImageMenus = false,
}: {
  src: string
  alt?: string
  headerLabel: string
  angular: boolean
  /** 当前用户聚焦操作的一侧（点击/滚轮会切换） */
  isActive: boolean
  onActivate: () => void
  paneAriaLabel: string
  /** 右键、长按与本按钮：保存当前侧展示的图 */
  onSaveRequest?: () => void
  /** 若提供：由父级处理右键（如弹出菜单）；为 undefined 时不拦截，交给系统 */
  onContextMenu?: (e: MouseEvent) => void
  /** 与全局 {@link useImageInteractionPolicy} 对齐；为 false 时不使用程序化长按保存 */
  syntheticLongPress?: boolean
  /** 微信环境：让图片可交互以触发系统长按菜单 */
  useEmbeddedNativeImageMenus?: boolean
}) {
  const saveGestures = useTouchLongPress(() => onSaveRequest?.(), {
    disabled: !onSaveRequest || !syntheticLongPress,
  })
  const [loaded, setLoaded] = useState(false)
  const [broken, setBroken] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  const paneHttpStatus = useBrokenImageHttpStatus(src, broken)
  const paneDiagnosis = useMemo(
    () => diagnoseImageLoadError({ url: src, httpStatus: paneHttpStatus }),
    [src, paneHttpStatus],
  )
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())
  const dragStartRef = useRef({ x: 0, y: 0 })
  const pinchStartRef = useRef<{ distance: number; zoom: number } | null>(null)
  const wheelTargetRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setLoaded(false)
    setBroken(false)
    setReloadTick(0)
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }, [src])

  useEffect(() => {
    const target = wheelTargetRef.current
    if (!target) return

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onActivate()
      setZoom((z) => {
        const next = applyWheelZoom(z, e.deltaY)
        if (next <= 1) setOffset({ x: 0, y: 0 })
        return next
      })
    }

    target.addEventListener('wheel', onWheel, { passive: false })
    return () => target.removeEventListener('wheel', onWheel)
  }, [onActivate])

  const resetLocal = useCallback(() => {
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }, [])

  const calcD = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y)
  const round = angular ? 'rounded-none' : 'rounded-lg'

  return (
    <div
      role="group"
      aria-label={paneAriaLabel}
      aria-current={isActive ? 'true' : undefined}
      className={[
        'relative z-0 flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden transition-[background-color,box-shadow] duration-150',
        isActive
          ? 'bg-[color-mix(in_srgb,var(--accent)_22%,var(--surface-muted))] shadow-[inset_0_0_0_2px_color-mix(in_srgb,var(--accent)_62%,var(--line))]'
          : 'bg-surface shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--line)_55%,transparent)]',
      ].join(' ')}
    >
      <div
        className={[
          'flex min-w-0 shrink-0 items-center gap-x-1 border-b px-1.5 py-1.5 transition-colors sm:gap-x-2 sm:px-2',
          isActive
            ? 'border-[color-mix(in_srgb,var(--accent)_40%,var(--line))] bg-[color-mix(in_srgb,var(--accent)_12%,var(--panel))]'
            : 'border-line bg-[color-mix(in_srgb,var(--surface-muted)_94%,var(--panel))]',
        ].join(' ')}
      >
        <span
          className={[
            'min-w-0 flex-1 truncate text-[9px] font-semibold uppercase tracking-wide sm:text-[11px]',
            isActive ? 'text-ink' : 'text-ink-soft',
          ].join(' ')}
        >
          {headerLabel}
        </span>
        {isActive ? (
          <span
            className={[
              'shrink-0 rounded border border-[color-mix(in_srgb,var(--accent)_48%,var(--line))]',
              'bg-[color-mix(in_srgb,var(--accent)_20%,var(--panel))]',
              'px-1 py-px text-[8px] font-bold uppercase tracking-wide text-ink',
              angular ? 'rounded-none' : 'rounded-md',
            ].join(' ')}
          >
            操作中
          </span>
        ) : null}
        <ChromeMonoTag angular={angular} className="shrink-0 text-[10px] tabular-nums sm:text-[11px]">
          {`${Math.round(zoom * 100)}%`}
        </ChromeMonoTag>
        {onSaveRequest ? (
          <button
            type="button"
            className={[
              'shrink-0 border-0 bg-transparent p-1 text-ink-soft transition hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-ink',
              angular ? 'rounded-none' : 'rounded-md',
            ].join(' ')}
            aria-label="保存本侧图片"
            onClick={(e) => {
              e.stopPropagation()
              onSaveRequest()
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          </button>
        ) : null}
        <button
          type="button"
          className={[
            'shrink-0 border-0 bg-transparent p-1 text-ink-soft transition hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-ink',
            angular ? 'rounded-none' : 'rounded-md',
          ].join(' ')}
          aria-label="重置本侧缩放与位移"
          onClick={(e) => {
            e.stopPropagation()
            resetLocal()
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>
      <div
        ref={wheelTargetRef}
        className="relative flex min-h-0 w-full min-w-0 flex-1 touch-none items-center justify-center overflow-hidden p-1 sm:p-2"
        style={{
          cursor:
            zoom > 1 ? (dragging ? 'grabbing' : 'grab') : zoom < 1 ? 'zoom-out' : 'zoom-in',
        }}
        onContextMenu={onContextMenu}
        onDoubleClick={(e) => {
          e.stopPropagation()
          onActivate()
          if (zoom !== 1) resetLocal()
          else setZoom(2)
        }}
        onPointerDown={(e) => {
          saveGestures.onPointerDown(e)
          if (e.pointerType === 'mouse' && e.button !== 0) return
          onActivate()
          pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
          if (pointersRef.current.size === 1 && zoom > 1) {
            e.currentTarget.setPointerCapture(e.pointerId)
            setDragging(true)
            dragStartRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y }
          } else if (pointersRef.current.size === 2) {
            const pts = Array.from(pointersRef.current.values())
            pinchStartRef.current = { distance: calcD(pts[0], pts[1]), zoom }
            setDragging(false)
          }
        }}
        onPointerMove={(e) => {
          saveGestures.onPointerMove(e)
          if (!pointersRef.current.has(e.pointerId)) return
          pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
          if (pointersRef.current.size === 2 && pinchStartRef.current) {
            const [a, b] = Array.from(pointersRef.current.values())
            const dist = calcD(a, b)
            const ratio = dist / pinchStartRef.current.distance
            const nextZoom = clampZoomIn(+(pinchStartRef.current.zoom * ratio).toFixed(3))
            setZoom(nextZoom)
            if (nextZoom <= 1) setOffset({ x: 0, y: 0 })
            return
          }
          if (pointersRef.current.size === 1 && dragging && zoom > 1) {
            const next = {
              x: e.clientX - dragStartRef.current.x,
              y: e.clientY - dragStartRef.current.y,
            }
            setOffset(clampCompareOffset(next, zoom, wheelTargetRef.current))
          }
        }}
        onPointerUp={(e) => {
          saveGestures.onPointerUp(e)
          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId)
          }
          pointersRef.current.delete(e.pointerId)
          if (pointersRef.current.size < 2) pinchStartRef.current = null
          if (pointersRef.current.size === 0) setDragging(false)
        }}
        onPointerCancel={(e) => {
          saveGestures.onPointerCancel(e)
          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId)
          }
          pointersRef.current.delete(e.pointerId)
          if (pointersRef.current.size < 2) pinchStartRef.current = null
          if (pointersRef.current.size === 0) setDragging(false)
        }}
      >
        <div className="relative isolate h-full w-full min-h-0 min-w-0 overflow-hidden">
          {/* 骨架屏 - 仅在未加载且未失败时显示 */}
          {!loaded && !broken && (
            <div className={`glyphicai-skeleton-shimmer absolute inset-0 z-[1] ${round}`} aria-hidden />
          )}
          
          {/* 错误状态 - 加载失败时显示 */}
          {broken && (
            <div className={`pointer-events-auto absolute inset-0 z-[2] flex items-center justify-center ${round}`}>
              <ImageLoadFailurePlaceholder
                angular={angular}
                httpStatus={paneHttpStatus}
                reason={paneDiagnosis.reasonShort}
                urlLabel={paneDiagnosis.safeUrlLabel}
                onReload={() => {
                  setReloadTick((t) => t + 1)
                  setBroken(false)
                  setLoaded(false)
                }}
                className="justify-center"
              />
            </div>
          )}
          
          {/* 图片 - 始终渲染但透明度控制显示 */}
          <img
            key={reloadTick}
            src={src}
            alt={useEmbeddedNativeImageMenus ? '图片预览，长按可保存' : alt}
            className={[
              'absolute left-1/2 top-1/2 z-[2] max-h-full max-w-full -translate-x-1/2 -translate-y-1/2 select-none object-contain transition-opacity duration-200',
              useEmbeddedNativeImageMenus ? 'pointer-events-auto' : 'pointer-events-none',
              loaded && !broken ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
            style={{
              transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
              transformOrigin: 'center center',
            }}
            draggable={false}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => {
              setBroken(true)
              setLoaded(true)
            }}
          />
        </div>
      </div>
    </div>
  )
}

function ReferenceThumbButton({
  url,
  index,
  angular,
  selected,
  comparingOpen,
  onSelectCompare,
  bridge,
}: {
  url: string
  index: number
  angular: boolean
  selected: boolean
  /** 是否处于分栏对比中（用于「对比中」角标与加粗选中态） */
  comparingOpen: boolean
  onSelectCompare: () => void
  /** 与预览主图一致的保存/复制/打开菜单；微信内改为原生 `img` 长按 */
  bridge: {
    policy: ImageInteractionPolicy
    openDesktop: (e: MouseEvent, src: string, stem: string) => void
    openSheet: (src: string, stem: string) => void
  }
}) {
  const [loaded, setLoaded] = useState(false)
  const [broken, setBroken] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  const displayUrl = resolveReferenceImageDisplayUrl(url)
  /** 侧栏略大图用较高 imageView 宽度；签名私有链原样返回 */
  const thumbSrc = referenceImageThumbnailUrl(displayUrl, 960)
  const thumbHttpStatus = useBrokenImageHttpStatus(thumbSrc, broken)
  const thumbDiagnosis = useMemo(
    () => diagnoseImageLoadError({ url: thumbSrc, httpStatus: thumbHttpStatus }),
    [thumbSrc, thumbHttpStatus],
  )
  const round = angular ? 'rounded-none' : 'rounded-md'
  const saveStem = `ref${index + 1}_aside`
  const showMobileSheetChrome =
    !bridge.policy.hijackContextMenu && !bridge.policy.useEmbeddedNativeImageMenus

  useEffect(() => {
    setLoaded(false)
    setBroken(false)
    setReloadTick(0)
  }, [thumbSrc])
  const activeInCompare = comparingOpen && selected

  const shellClass = [
    'relative h-[9rem] w-[9.5rem] shrink-0 overflow-hidden bg-surface-muted transition-all duration-150',
    round,
    activeInCompare
      ? 'z-[1] scale-[1.02] border-[3px] border-accent bg-[color-mix(in_srgb,var(--accent)_12%,var(--surface-muted))] shadow-[var(--shadow-card)]'
      : selected
        ? 'border-2 border-accent'
        : 'border-2 border-line hover:border-[color-mix(in_srgb,var(--line)_45%,var(--accent)_55%)]',
  ].join(' ')

  const innerBadges = (
    <>
      {activeInCompare ? (
        <span className="pointer-events-none absolute left-0 right-0 top-0 z-[4] border-b border-line/65 bg-panel py-px text-center text-[10px] font-bold text-accent shadow-sm">
          对比中
        </span>
      ) : null}
      {!loaded && !broken ? (
        <div className={`glyphicai-skeleton-shimmer absolute inset-0 z-[1] ${round}`} aria-hidden />
      ) : null}
      {broken ? (
        <ImageLoadFailurePlaceholder
          angular={angular}
          compact
          httpStatus={thumbHttpStatus}
          reason={thumbDiagnosis.reasonShort}
          urlLabel={thumbDiagnosis.safeUrlLabel}
          onReload={() => {
            setReloadTick((t) => t + 1)
            setBroken(false)
            setLoaded(false)
          }}
          className={`pointer-events-auto absolute inset-0 z-[2] ${round}`}
        />
      ) : (
        <img
          key={reloadTick}
          src={thumbSrc}
          alt=""
          loading="eager"
          decoding="async"
          fetchPriority="high"
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => {
            setBroken(true)
            setLoaded(true)
          }}
          className={[
            'relative z-[2] h-full w-full object-cover transition-opacity duration-200',
            bridge.policy.useEmbeddedNativeImageMenus ? 'cursor-pointer pointer-events-auto' : '',
            activeInCompare ? 'brightness-[1.03]' : '',
            loaded ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
          onClick={
            bridge.policy.useEmbeddedNativeImageMenus
              ? (e) => {
                  e.stopPropagation()
                  onSelectCompare()
                }
              : undefined
          }
        />
      )}
      <span className="pointer-events-none absolute bottom-0.5 left-0.5 z-[3] rounded bg-ink/75 px-1 py-px text-[9px] font-medium text-panel">
        {index + 1}
      </span>
    </>
  )

  if (bridge.policy.useEmbeddedNativeImageMenus) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={shellClass}
        aria-label={`参考图 ${index + 1}，点击与主图对比`}
        aria-pressed={selected}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelectCompare()
          }
        }}
      >
        {innerBadges}
      </div>
    )
  }

  // 图片加载失败时使用 div 避免按钮嵌套问题
  const Wrapper = broken ? 'div' : 'button'
  const wrapperProps = broken
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `参考图 ${index + 1}，点击与主图对比`,
        'aria-pressed': selected,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelectCompare()
          }
        },
        onClick: onSelectCompare,
      }
    : {
        type: 'button' as const,
        onClick: onSelectCompare,
        'aria-label': `参考图 ${index + 1}，点击与主图对比`,
        'aria-pressed': selected,
        onContextMenu: bridge.policy.hijackContextMenu
          ? (e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              bridge.openDesktop(e, displayUrl, saveStem)
            }
          : undefined,
      }

  return (
    <Wrapper {...wrapperProps} className={shellClass}>
      {innerBadges}
      {!broken && showMobileSheetChrome ? (
        <div className="pointer-events-none absolute bottom-0 right-0 z-[10] flex origin-bottom-right scale-[0.72]">
          <div className="pointer-events-auto">
            <ImageMoreActionsTrigger
              angular={angular}
              label={`参考图 ${index + 1} 更多操作`}
              onOpen={() => bridge.openSheet(displayUrl, saveStem)}
            />
          </div>
        </div>
      ) : null}
    </Wrapper>
  )
}

/**
 * 全屏图片查看：遮罩与结构与 ThemeModal 对齐（theme-modal-backdrop、border-line / surface / panel）；
 * 大屏下左图右提示词，便于多图横向切换时保持垂直空间。
 */
export function ImageLightbox({
  open,
  src,
  alt = '',
  items,
  index,
  onIndexChange,
  angular = false,
  onRefreshImageUrl,
  onClose,
}: Props) {
  const prefersReduced = useReducedMotion()
  const motionPreset = useThemeMotion()
  const preset = mergeReducedMotionPreset(motionPreset, !!prefersReduced)

  const ease = [...preset.easing] as [number, number, number, number]
  const panelDur = Math.min(0.42, Math.max(0.24, preset.expandDuration * 0.72))
  const enterDuration = Math.min(0.35, Math.max(0.18, preset.duration * 0.65))
  const enterScale = preset.enterScale
  const enterY = prefersReduced ? 0 : preset.enterY
  const [zoom, setZoom] = useState(1)
  const [localIndex, setLocalIndex] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [hiding, setHiding] = useState(false)
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map())
  const dragStartRef = useRef({ x: 0, y: 0 })
  const pinchStartRef = useRef<{ distance: number; zoom: number } | null>(null)
  const wheelTargetRef = useRef<HTMLDivElement | null>(null)
  /** 与主图分栏对比：对应 referenceImageUrls 下标 */
  const [compareRefIndex, setCompareRefIndex] = useState<number | null>(null)
  const compareRefIndexRef = useRef<number | null>(null)
  compareRefIndexRef.current = compareRefIndex
  /** 对比模式下用户当前操作侧（点击/滚轮切换） */
  const [compareActivePane, setCompareActivePane] = useState<'main' | 'reference'>('main')
  const isLgAside = useMediaQuery('(min-width: 1024px)')
  /** 窄屏：侧栏信息可收起，对比时默认收起以腾出纵向空间 */
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(true)
  /** 对比模式：左侧所占宽度比例（20%–80%），中间竖条拖动调整 */
  const [compareSplitPct, setCompareSplitPct] = useState(50)
  const compareRowRef = useRef<HTMLDivElement>(null)
  const compareSplitDragRef = useRef<{ pointerId: number; startX: number; startPct: number } | null>(
    null,
  )

  const normalizedItems = useMemo<ImageLightboxItem[]>(() => {
    if (items?.length) return items.map((item) => normalizeImageLightboxItem(item))
    if (!src) return []
    return [normalizeImageLightboxItem({ src, alt })]
  }, [alt, items, src])

  const activeIndex = index ?? localIndex
  const active = normalizedItems[activeIndex] ?? normalizedItems[0]
  const lastActiveRef = useRef<ImageLightboxItem | undefined>(undefined)
  if (active) {
    lastActiveRef.current = active
  }
  const displayActive = open ? active : lastActiveRef.current
  const canSwitch = normalizedItems.length > 1
  const [mainImageBroken, setMainImageBroken] = useState(false)
  const [mainReloadTick, setMainReloadTick] = useState(0)
  /** 正在向后端要新签名（不直接甩错误给用户） */
  const [mainHealing, setMainHealing] = useState(false)

  /** 主图仅在「加载失败」时用 objectKey 换一份新签名重试；不主动重签（点得开即说明列表签名有效） */
  const [healedSrc, setHealedSrc] = useState<string | null>(null)
  const activeItemRef = useRef<ImageLightboxItem | undefined>(undefined)
  activeItemRef.current = displayActive
  const activeObjectKey = (displayActive?.objectKey ?? '').trim()
  const canAutoSign = Boolean(activeObjectKey) && typeof onRefreshImageUrl === 'function'
  const baseActiveSrc = displayActive?.src ?? ''
  const activeDisplaySrc = healedSrc || baseActiveSrc
  const activeSrcRef = useRef<string>('')
  activeSrcRef.current = baseActiveSrc

  const refreshExecutor: RefreshUrlExecutor = useCallback(
    (objectKey: string) => {
      const item = activeItemRef.current
      if (!item || !onRefreshImageUrl) return Promise.resolve(null)
      return onRefreshImageUrl({ ...item, objectKey })
    },
    [onRefreshImageUrl],
  )

  /** 同一张图在一次会话里最多自动补签一次（手动点重载可再来一次） */
  const autoHealedKeysRef = useRef<Set<string>>(new Set())

  const mainHttpStatus = useBrokenImageHttpStatus(activeDisplaySrc, mainImageBroken)
  const mainImageDiagnosis = useMemo(
    () => diagnoseImageLoadError({ url: activeDisplaySrc, httpStatus: mainHttpStatus }),
    [activeDisplaySrc, mainHttpStatus],
  )

  /** 加载失败：先补一份新签名再试，补完还是不行（或无法补签）才展示错误占位 */
  const handleMainImageError = useCallback(() => {
    if (canAutoSign && activeObjectKey && !autoHealedKeysRef.current.has(activeObjectKey)) {
      autoHealedKeysRef.current.add(activeObjectKey)
      setMainHealing(true)
      void refreshReferenceUrlOnce(activeObjectKey, refreshExecutor, { force: true })
        .then((next) => {
          const url = next?.url ? resolveReferenceImageDisplayUrl(next.url) : ''
          if (url && url !== activeSrcRef.current) {
            setHealedSrc(url)
            setMainReloadTick((t) => t + 1)
            setMainImageBroken(false)
            return
          }
          setMainImageBroken(true)
        })
        .catch(() => {
          setMainImageBroken(true)
        })
        .finally(() => {
          setMainHealing(false)
        })
      return
    }
    setMainHealing(false)
    setMainImageBroken(true)
  }, [canAutoSign, activeObjectKey, refreshExecutor])

  /** 占位上的「重新加载」：优先补新签名，无法补签时退回同地址重挂载 */
  const handleMainReload = useCallback(() => {
    if (canAutoSign && activeObjectKey) {
      autoHealedKeysRef.current.delete(activeObjectKey)
      handleMainImageError()
      return
    }
    setMainReloadTick((t) => t + 1)
    setMainImageBroken(false)
  }, [canAutoSign, activeObjectKey, handleMainImageError])

  const setIndex = useCallback((next: number) => {
    const max = normalizedItems.length - 1
    const clamped = Math.max(0, Math.min(max, next))
    onIndexChange?.(clamped)
    if (index === undefined) setLocalIndex(clamped)
    setZoom(1)
    setOffset({ x: 0, y: 0 })
    setCompareRefIndex(null)
    setMainImageBroken(false)
    setMainReloadTick(0)
  }, [index, normalizedItems.length, onIndexChange])

  useEffect(() => {
    setMainImageBroken(false)
    setMainReloadTick(0)
    setMainHealing(false)
  }, [activeDisplaySrc, activeObjectKey])

  /** 切换图片 / 打开关闭灯箱时清空「自愈新签名」，回到列表原始签名地址 */
  useEffect(() => {
    setHealedSrc(null)
  }, [activeObjectKey, open])

  const clampOffset = (next: { x: number; y: number }, z: number) => {
    if (z <= 1) return { x: 0, y: 0 }
    const container = wheelTargetRef.current
    if (!container) {
      const max = Math.max(48, (z - 1) * 260)
      return {
        x: Math.max(-max, Math.min(max, next.x)),
        y: Math.max(-max, Math.min(max, next.y)),
      }
    }
    const img = container.querySelector('img')
    if (!img) {
      const max = Math.max(48, (z - 1) * 260)
      return {
        x: Math.max(-max, Math.min(max, next.x)),
        y: Math.max(-max, Math.min(max, next.y)),
      }
    }
    const containerRect = container.getBoundingClientRect()
    const imgRect = img.getBoundingClientRect()
    const scaledW = imgRect.width
    const scaledH = imgRect.height
    const maxOffsetX = Math.max(0, (scaledW - containerRect.width) / 2)
    const maxOffsetY = Math.max(0, (scaledH - containerRect.height) / 2)
    return {
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, next.x)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, next.y)),
    }
  }

  const resetView = () => {
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }

  const calcDistance = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y)

  const referenceUrlsIdentityKey = active?.referenceImageUrls?.join('\u0001') ?? ''

  useEffect(() => {
    setCompareRefIndex(null)
  }, [activeIndex, activeDisplaySrc, referenceUrlsIdentityKey])

  useEffect(() => {
    if (compareRefIndex !== null) {
      setCompareActivePane('main')
    }
  }, [compareRefIndex])

  const hasAside =
    normalizedItems.length > 1 ||
    !!(active?.prompt?.trim() ||
      (active?.details && active.details.length > 0) ||
      (active?.referenceImageUrls && active.referenceImageUrls.length > 0))

  const compareRefUrl =
    active && compareRefIndex !== null && active.referenceImageUrls?.[compareRefIndex]
      ? resolveReferenceImageDisplayUrl(active.referenceImageUrls[compareRefIndex])
      : null
  const comparing = compareRefUrl != null

  /** 供快捷键 Ctrl/⌘+C 读取，避免把 compareActivePane 等加入 open  effect 依赖导致误重置视图 */
  const copySourceRef = useRef({
    comparing: false,
    pane: 'main' as 'main' | 'reference',
    mainSrc: undefined as string | undefined,
    refUrl: null as string | null,
    mainBroken: false,
  })
  copySourceRef.current = {
    comparing,
    pane: compareActivePane,
    mainSrc: activeDisplaySrc,
    refUrl: compareRefUrl,
    mainBroken: mainImageBroken,
  }

  const { showToast } = useToast()
  const policy = useImageInteractionPolicy()
  const ctx = useImageContextPayload()
  // closeAll 是 useCallback(空依赖)，引用稳定；不要把整个 ctx 对象放 effect 依赖——它每次 render 新建
  const closeContextMenus = ctx.closeAll

  const previewSaveStem = useMemo(() => {
    const raw =
      active?.prompt?.trim() ||
      active?.taskLabel?.trim() ||
      active?.subtitle?.trim() ||
      'glyphic_preview'
    const s = raw.slice(0, 80).replace(/[/\\?*:|"<>]/g, '_').trim()
    return s || 'glyphic_preview'
  }, [active?.prompt, active?.subtitle, active?.taskLabel])

  const savePreviewImage = useCallback(
    async (url: string | undefined, suffix: string) => {
      const u = url?.trim()
      if (!u) return
      try {
        const mode = await triggerBrowserImageDownload(u, `${previewSaveStem}_${suffix}`)
        showToast(toastForBrowserImageDownload(mode))
      } catch (e) {
        showToast({ message: e instanceof Error ? e.message : '保存失败', variant: 'error' })
      }
    },
    [previewSaveStem, showToast],
  )

  const copyPreviewImage = useCallback(
    async (url: string | undefined) => {
      const u = url?.trim()
      if (!u) return
      try {
        await copyImageToClipboard(u)
        showToast({ message: '已复制图片', variant: 'success', autoCloseMs: 2800 })
      } catch (e) {
        showToast({ message: e instanceof Error ? e.message : '复制失败', variant: 'error' })
      }
    },
    [showToast],
  )

  const menuItems = useMemo(() => {
    const payload = ctx.desktop ?? ctx.sheet
    if (!payload) return []
    return buildImageContextMenuItems({
      imageUrl: payload.src,
      onSave: async () => {
        await savePreviewImage(payload.src, payload.stem)
      },
      showToast,
    })
  }, [ctx.desktop, ctx.sheet, savePreviewImage, showToast])

  const mainPaneSavePress = useTouchLongPress(
    () => void savePreviewImage(activeDisplaySrc, `p${activeIndex + 1}`),
    {
      disabled:
        comparing || mainImageBroken || !activeDisplaySrc || !policy.syntheticLongPressSave,
    },
  )

  useEffect(() => {
    if (!open) closeContextMenus()
  }, [open, closeContextMenus])

  useEffect(() => {
    if (policy.useEmbeddedNativeImageMenus) closeContextMenus()
  }, [policy.useEmbeddedNativeImageMenus, closeContextMenus])

  useEffect(() => {
    if (!isLgAside && comparing && hasAside) setMobileDetailsOpen(false)
  }, [comparing, hasAside, isLgAside])

  useEffect(() => {
    if (!comparing && !isLgAside && hasAside) setMobileDetailsOpen(true)
  }, [comparing, hasAside, isLgAside])

  useEffect(() => {
    if (!open) return
    resetView()
    setMainImageBroken(false)
    setCompareRefIndex(null)
    setMobileDetailsOpen(true)
    setCompareSplitPct(50)
    if (index !== undefined) setLocalIndex(index)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (ctx.desktop != null || ctx.sheet != null) {
          closeContextMenus()
          return
        }
        if (compareRefIndexRef.current !== null) {
          setCompareRefIndex(null)
          return
        }
        onClose()
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        const ae = document.activeElement
        if (
          ae instanceof HTMLInputElement ||
          ae instanceof HTMLTextAreaElement ||
          ae instanceof HTMLSelectElement ||
          (ae instanceof HTMLElement && ae.isContentEditable)
        ) {
          return
        }
        if (window.getSelection()?.toString().trim()) return

        const s = copySourceRef.current
        const urlToCopy = s.comparing
          ? s.pane === 'main'
            ? s.mainSrc
            : s.refUrl ?? undefined
          : s.mainSrc
        if (!urlToCopy?.trim()) return
        if (!s.comparing && s.mainBroken) return
        e.preventDefault()
        void copyPreviewImage(urlToCopy)
        return
      }
      if (e.key === '+' || e.key === '=') setZoom((z) => clampZoomIn(+(z + 0.2).toFixed(2)))
      if (e.key === '-') setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.2).toFixed(2)))
      if (e.key === '0') resetView()
      if (canSwitch && e.key === 'ArrowLeft') setIndex(activeIndex - 1)
      if (canSwitch && e.key === 'ArrowRight') setIndex(activeIndex + 1)
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [activeIndex, canSwitch, closeContextMenus, copyPreviewImage, index, onClose, open, setIndex])

  useEffect(() => {
    if (open) setHiding(false)
  }, [open])

  const handleClose = useCallback(() => {
    setHiding(true)
    onClose()
  }, [onClose])

  useEffect(() => {
    const target = wheelTargetRef.current
    if (!target || comparing || !open) return

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setZoom((z) => {
        const next = applyWheelZoom(z, e.deltaY)
        if (next <= 1) setOffset({ x: 0, y: 0 })
        return next
      })
    }

    target.addEventListener('wheel', onWheel, { passive: false })
    return () => target.removeEventListener('wheel', onWheel)
  }, [comparing, open])

  if (typeof document === 'undefined') return null
  if (!displayActive) return null

  const roundBtn = angular ? 'rounded-none' : 'rounded-lg'
  const chromeIconBtn = `shrink-0 border border-line bg-panel p-2 text-ink shadow-sm transition hover:bg-surface-muted ${roundBtn}`

  const easeCss = `cubic-bezier(${ease.join(', ')})`
  const enterAnimation = prefersReduced
    ? 'lightbox-enter-reduced'
    : `lightbox-enter ${enterDuration}s ${easeCss}`

  const cssVars = {
    '--lightbox-enter-scale': enterScale,
    '--lightbox-enter-y': `${enterY}px`,
  } as React.CSSProperties

  return createPortal(
    open ? (
      <div
        key="lightbox-shell"
        className={`fixed inset-0 z-[200] flex h-dvh w-screen ${hiding ? 'opacity-0 pointer-events-none' : ''}`}
        style={{
          ...cssVars,
          animation: hiding ? undefined : enterAnimation,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="图片预览"
      >
        <button
          type="button"
          aria-label="关闭预览"
          className="absolute inset-0 bg-black/60"
          style={{
            animation: hiding ? undefined : enterAnimation,
          }}
          onClick={handleClose}
        />
        <div
          className="relative z-[1] flex h-dvh w-screen flex-col overflow-hidden border border-line bg-panel text-ink shadow-card"
          style={{
            animation: hiding ? undefined : (prefersReduced
              ? 'lightbox-panel-enter-reduced'
              : `lightbox-panel-enter ${enterDuration}s ${easeCss}`),
            transformOrigin: 'center center',
          }}
          onClick={(e) => e.stopPropagation()}
        >
            <div
              className={[
                'flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-line bg-surface-muted px-3 py-2 sm:px-4',
              ].join(' ')}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-ink sm:text-sm">
                  {displayActive.taskLabel ?? '图片预览'}
                </p>
                {displayActive.subtitle ? (
                  <p className="truncate text-[10px] text-ink-soft sm:text-[11px]">{displayActive.subtitle}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {comparing ? (
                  <>
                    <ChromeMonoTag angular={angular} className="max-w-[10rem] truncate sm:max-w-none">
                      对比模式
                    </ChromeMonoTag>
                    {/* 右上角常驻被对比参考图的迷你预览，方便确认当前对比对象 */}
                    <img
                      src={compareRefUrl ?? ''}
                      alt=""
                      aria-hidden
                      draggable={false}
                      className={[
                        'h-8 w-11 shrink-0 object-cover ring-1 ring-line',
                        angular ? 'rounded-none' : 'rounded-md',
                      ].join(' ')}
                    />
                  </>
                ) : (
                  <LightboxRatioTag angular={angular} zoom={zoom} />
                )}
                {!comparing && activeDisplaySrc && !mainImageBroken ? (
                  <>
                    <button
                      type="button"
                      className={chromeIconBtn}
                      aria-label="保存当前图片"
                      onClick={() => void savePreviewImage(activeDisplaySrc, `p${activeIndex + 1}`)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <path d="M7 10l5 5 5-5" />
                        <path d="M12 15V3" />
                      </svg>
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className={chromeIconBtn}
                  aria-label={comparing ? '退出对比' : '重置缩放'}
                  onClick={() => (comparing ? setCompareRefIndex(null) : resetView())}
                >
                  {comparing ? (
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
                      <path d="M11 12H4" />
                      <path d="m7 9-3 3 3 3" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M3 12a9 9 0 1 0 3-6.7" />
                      <path d="M3 3v5h5" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  className={chromeIconBtn}
                  aria-label="关闭预览"
                  onClick={handleClose}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row lg:items-stretch">
              <div
                ref={wheelTargetRef}
                className={[
                  'relative flex min-w-0 touch-none flex-col overflow-hidden bg-surface',
                  isLgAside
                    ? 'min-h-0 flex-1 lg:max-w-[calc(100%-min(380px,38%))]'
                    : hasAside && mobileDetailsOpen
                      ? 'min-h-[36dvh] flex-1 sm:min-h-[40dvh]'
                      : 'min-h-0 flex-1',
                ].join(' ')}
                onContextMenu={
                  comparing || mainImageBroken || !activeDisplaySrc
                    ? undefined
                    : policy.hijackContextMenu
                      ? (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          ctx.openDesktop(e, activeDisplaySrc, `p${activeIndex + 1}`)
                        }
                      : undefined
                }
                onDoubleClick={
                  comparing
                    ? undefined
                    : () => {
                        if (zoom !== 1) resetView()
                        else setZoom(2)
                      }
                }
                onPointerDown={
                  comparing
                    ? undefined
                    : (e) => {
                        mainPaneSavePress.onPointerDown(e)
                        if (e.pointerType === 'mouse' && e.button !== 0) return
                        const nextPoint = { x: e.clientX, y: e.clientY }
                        pointersRef.current.set(e.pointerId, nextPoint)
                        if (pointersRef.current.size === 1 && zoom > 1) {
                          e.currentTarget.setPointerCapture(e.pointerId)
                          setDragging(true)
                          dragStartRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y }
                        } else if (pointersRef.current.size === 2) {
                          const pts = Array.from(pointersRef.current.values())
                          pinchStartRef.current = { distance: calcDistance(pts[0], pts[1]), zoom }
                          setDragging(false)
                        }
                      }
                }
                onPointerMove={
                  comparing
                    ? undefined
                    : (e) => {
                        mainPaneSavePress.onPointerMove(e)
                        if (!pointersRef.current.has(e.pointerId)) return
                        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
                        if (pointersRef.current.size === 2 && pinchStartRef.current) {
                          const [a, b] = Array.from(pointersRef.current.values())
                          const dist = calcDistance(a, b)
                          const ratio = dist / pinchStartRef.current.distance
                          const nextZoom = clampZoomIn(
                            +(pinchStartRef.current.zoom * ratio).toFixed(3),
                          )
                          setZoom(nextZoom)
                          if (nextZoom <= 1) setOffset({ x: 0, y: 0 })
                          return
                        }
                        if (pointersRef.current.size === 1 && dragging && zoom > 1) {
                          const next = {
                            x: e.clientX - dragStartRef.current.x,
                            y: e.clientY - dragStartRef.current.y,
                          }
                          setOffset(clampOffset(next, zoom))
                        }
                      }
                }
                onPointerUp={
                  comparing
                    ? undefined
                    : (e) => {
                        mainPaneSavePress.onPointerUp(e)
                        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                          e.currentTarget.releasePointerCapture(e.pointerId)
                        }
                        pointersRef.current.delete(e.pointerId)
                        if (pointersRef.current.size < 2) pinchStartRef.current = null
                        if (pointersRef.current.size === 0) setDragging(false)
                      }
                }
                onPointerCancel={
                  comparing
                    ? undefined
                    : (e) => {
                        mainPaneSavePress.onPointerCancel(e)
                        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                          e.currentTarget.releasePointerCapture(e.pointerId)
                        }
                        pointersRef.current.delete(e.pointerId)
                        if (pointersRef.current.size < 2) pinchStartRef.current = null
                        if (pointersRef.current.size === 0) setDragging(false)
                      }
                }
                style={
                  comparing
                    ? undefined
                    : {
                        cursor:
                          zoom > 1
                            ? dragging
                              ? 'grabbing'
                              : 'grab'
                            : zoom < 1
                              ? 'zoom-out'
                              : 'zoom-in',
                      }
                }
              >
                {!comparing ? (
                  <>
                    {canSwitch ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIndex(activeIndex - 1)}
                          disabled={activeIndex <= 0}
                          className="absolute inset-y-0 left-0 z-[15] w-[18%] min-w-16 bg-transparent disabled:opacity-30"
                          aria-label="上一张"
                        >
                          <span
                            className={[
                              'pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 border border-line/80 bg-[color-mix(in_srgb,var(--panel)_92%,transparent)] p-1.5 text-ink shadow-sm backdrop-blur-[2px]',
                              roundBtn,
                            ].join(' ')}
                          >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="m15 18-6-6 6-6" />
                            </svg>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setIndex(activeIndex + 1)}
                          disabled={activeIndex >= normalizedItems.length - 1}
                          className="absolute inset-y-0 right-0 z-[15] w-[18%] min-w-16 bg-transparent disabled:opacity-30"
                          aria-label="下一张"
                        >
                          <span
                            className={[
                              'pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 border border-line/80 bg-[color-mix(in_srgb,var(--panel)_92%,transparent)] p-1.5 text-ink shadow-sm backdrop-blur-[2px]',
                              roundBtn,
                            ].join(' ')}
                          >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="m9 18 6-6-6-6" />
                            </svg>
                          </span>
                        </button>
                      </>
                    ) : null}
                    <div className="relative z-0 flex min-h-0 flex-1 p-2 sm:p-3">
                      <AnimatePresence mode="wait">
                        {mainImageBroken ? (
                          <motion.div
                            key="error-placeholder"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: preset.duration * 0.4, ease }}
                            className="pointer-events-auto absolute inset-0 z-[5] flex items-center justify-center"
                          >
                            <ImageLoadFailurePlaceholder
                              angular={angular}
                              httpStatus={mainHttpStatus}
                              reason={
                                mainHealing
                                  ? '正在向服务端申请新的访问签名，请稍候…'
                                  : mainImageDiagnosis.reasonShort
                              }
                              urlLabel={mainImageDiagnosis.safeUrlLabel}
                              onReload={handleMainReload}
                              reloadBusy={mainHealing}
                              className="justify-center"
                            />
                          </motion.div>
                        ) : (
                          <motion.img
                            key={`${activeDisplaySrc}-${mainReloadTick}`}
                            src={activeDisplaySrc}
                            alt={policy.useEmbeddedNativeImageMenus ? '图片预览，长按可保存' : (displayActive.alt ?? '')}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: offset.x, y: offset.y, scale: zoom }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: preset.duration * 0.5, ease }}
                            className={[
                              'absolute inset-0 z-0 h-full w-full max-h-full max-w-full select-none object-contain',
                              policy.useEmbeddedNativeImageMenus ? 'pointer-events-auto' : 'pointer-events-none',
                            ].join(' ')}
                            style={{
                              transformOrigin: 'center center',
                            }}
                            draggable={false}
                            decoding="async"
                            onError={handleMainImageError}
                          />
                        )}
                      </AnimatePresence>
                      {mainHealing ? (
                        <div className="pointer-events-none absolute inset-0 z-[4] flex items-center justify-center">
                          <span className="rounded-md border border-line/45 bg-[color-mix(in_srgb,var(--panel)_88%,transparent)] px-3 py-1.5 text-xs text-ink-soft shadow-sm backdrop-blur-[2px]">
                            访问凭证已过期，正在重新获取…
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <motion.div
                    ref={compareRowRef}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: preset.duration * 0.5, ease }}
                    className="flex min-h-0 min-w-0 flex-1 flex-row items-stretch"
                  >
                    <motion.div
                      layout
                      transition={{ duration: preset.duration * 0.4, ease }}
                      className="flex min-h-0 min-w-0 flex-col overflow-hidden"
                      style={{ flex: `0 0 ${compareSplitPct}%` }}
                    >
                      <CompareZoomablePane
                        src={activeDisplaySrc}
                        alt={displayActive.alt ?? ''}
                        headerLabel="当前预览"
                        angular={angular}
                        isActive={compareActivePane === 'main'}
                        onActivate={() => setCompareActivePane('main')}
                        paneAriaLabel="对比：当前预览图"
                        onSaveRequest={() =>
                          void savePreviewImage(activeDisplaySrc, `p${activeIndex + 1}_compare`)
                        }
                        onContextMenu={
                          policy.hijackContextMenu
                            ? (e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                ctx.openDesktop(e, activeDisplaySrc, `p${activeIndex + 1}_compare`)
                              }
                            : undefined
                        }
                        syntheticLongPress={policy.syntheticLongPressSave}
                        useEmbeddedNativeImageMenus={policy.useEmbeddedNativeImageMenus}
                      />
                    </motion.div>
                    <button
                      type="button"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="拖动调整左右对比区域宽度"
                      aria-valuemin={20}
                      aria-valuemax={80}
                      aria-valuenow={Math.round(compareSplitPct)}
                      className={[
                        'group relative z-10 w-2 shrink-0 touch-none select-none',
                        'cursor-col-resize border-0 bg-transparent p-0 outline-none',
                        'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--panel)]',
                      ].join(' ')}
                      onPointerDown={(e) => {
                        if (e.button !== 0) return
                        e.preventDefault()
                        e.stopPropagation()
                        compareSplitDragRef.current = {
                          pointerId: e.pointerId,
                          startX: e.clientX,
                          startPct: compareSplitPct,
                        }
                        ;(e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId)
                      }}
                      onPointerMove={(e) => {
                        const drag = compareSplitDragRef.current
                        if (!drag || e.pointerId !== drag.pointerId) return
                        const row = compareRowRef.current
                        if (!row) return
                        const w = row.getBoundingClientRect().width
                        if (w < 24) return
                        const deltaPct = ((e.clientX - drag.startX) / w) * 100
                        const next = drag.startPct + deltaPct
                        const clamped = Math.min(80, Math.max(20, next))
                        setCompareSplitPct(Math.round(clamped * 10) / 10)
                      }}
                      onPointerUp={(e) => {
                        if (compareSplitDragRef.current?.pointerId === e.pointerId) {
                          compareSplitDragRef.current = null
                        }
                        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                          e.currentTarget.releasePointerCapture(e.pointerId)
                        }
                      }}
                      onPointerCancel={(e) => {
                        if (compareSplitDragRef.current?.pointerId === e.pointerId) {
                          compareSplitDragRef.current = null
                        }
                        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                          e.currentTarget.releasePointerCapture(e.pointerId)
                        }
                      }}
                    >
                      <span
                        className="pointer-events-none absolute inset-y-1 left-1/2 w-px -translate-x-1/2 bg-line group-hover:bg-ink-soft/60 group-active:bg-ink-soft"
                        aria-hidden
                      />
                    </button>
                    <motion.div
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: preset.duration * 0.5, ease, delay: 0.1 }}
                      className="flex min-h-0 min-w-0 flex-col overflow-hidden"
                      style={{ flex: '1 1 0%' }}
                    >
                      <CompareZoomablePane
                        src={compareRefUrl!}
                        alt=""
                        headerLabel={`参考图 ${compareRefIndex! + 1}`}
                        angular={angular}
                        isActive={compareActivePane === 'reference'}
                        onActivate={() => setCompareActivePane('reference')}
                        paneAriaLabel={`对比：参考图 ${compareRefIndex! + 1}`}
                        onSaveRequest={() =>
                          void savePreviewImage(
                            compareRefUrl!,
                            `ref${(compareRefIndex ?? 0) + 1}_compare`,
                          )
                        }
                        onContextMenu={
                          policy.hijackContextMenu
                            ? (e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                ctx.openDesktop(
                                  e,
                                  compareRefUrl!,
                                  `ref${(compareRefIndex ?? 0) + 1}_compare`,
                                )
                              }
                            : undefined
                        }
                        syntheticLongPress={policy.syntheticLongPressSave}
                        useEmbeddedNativeImageMenus={policy.useEmbeddedNativeImageMenus}
                      />
                    </motion.div>
                  </motion.div>
                )}
              </div>

              <AnimatePresence mode="popLayout">
                {hasAside && (isLgAside || mobileDetailsOpen) ? (
                  <motion.aside
                    key="lightbox-aside"
                    id="image-lightbox-details"
                    initial={{ opacity: 0, x: isLgAside ? 24 : 0, y: isLgAside ? 0 : 16 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    exit={{ opacity: 0, x: isLgAside ? 24 : 0, y: isLgAside ? 0 : 16 }}
                    transition={{
                      duration: panelDur,
                      ease,
                    }}
                    className={[
                      'flex shrink-0 flex-col border-t border-line bg-surface lg:max-h-none lg:min-h-0 lg:w-[min(380px,38%)] lg:border-l lg:border-t-0',
                      isLgAside
                        ? 'max-h-none min-h-0 lg:shrink-0'
                        : 'max-h-[min(46dvh,26rem)] min-h-0',
                    ].join(' ')}
                  >
                    {!isLgAside ? (
                      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line bg-surface-muted px-3 py-2">
                        <span className="text-xs font-semibold text-ink">详情与参考图</span>
                        <button
                          type="button"
                          className={`border border-line bg-panel px-2 py-1 text-[11px] font-medium text-ink shadow-sm transition hover:bg-surface-muted ${roundBtn}`}
                          aria-label="收起详情面板"
                          onClick={() => setMobileDetailsOpen(false)}
                        >
                          收起
                        </button>
                      </div>
                    ) : null}
                    <div className="scroll-region min-h-0 flex-1 overflow-y-auto overscroll-contain ps-3 pe-3 py-2.5 text-xs leading-relaxed sm:ps-4 sm:pe-5 sm:py-3 sm:text-sm">
                      <AnimatePresence mode="popLayout">
                        {canSwitch ? (
                          <motion.div
                            key="grid-section"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: preset.duration * 0.5, ease }}
                            className="border-b border-line/70 pb-3"
                          >
                            <p className="text-[11px] font-medium text-ink sm:text-xs">
                              全部图片 · {normalizedItems.length} 张
                            </p>
                            <div className="scroll-region glyphicai-scrollbar-none mt-2 max-h-[13.5rem] min-h-0 overflow-y-auto overscroll-contain">
                              <div className="grid grid-cols-3 gap-2">
                              {normalizedItems.map((item, idx) => {
                                const gridActive = idx === activeIndex
                                return (
                                  <button
                                    key={`grid-${idx}-${item.src.slice(0, 48)}`}
                                    type="button"
                                    aria-label={`查看第 ${idx + 1} 张图片`}
                                    aria-current={gridActive ? 'true' : undefined}
                                    onClick={() => setIndex(idx)}
                                    className={[
                                      'relative aspect-square min-w-0 overflow-hidden bg-surface-muted transition',
                                      angular ? 'rounded-none' : 'rounded-md',
                                      gridActive
                                        ? 'ring-2 ring-accent'
                                        : 'ring-1 ring-line hover:ring-[color-mix(in_srgb,var(--accent)_55%,var(--line))]',
                                    ].join(' ')}
                                  >
                                    <img
                                      src={item.src}
                                      alt=""
                                      loading="lazy"
                                      decoding="async"
                                      draggable={false}
                                      className="h-full w-full object-cover"
                                    />
                                    <span className="pointer-events-none absolute bottom-0.5 left-0.5 rounded bg-ink/75 px-1 py-px text-[9px] font-medium text-panel">
                                      {idx + 1}
                                    </span>
                                  </button>
                                )
                              })}
                              </div>
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                      <AnimatePresence mode="popLayout">
                        {displayActive.prompt?.trim() ? (
                          <motion.div
                            key="prompt-section"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: preset.duration * 0.5, ease }}
                            className={
                              canSwitch || (displayActive.details && displayActive.details.length > 0)
                                ? 'border-b border-line/70 pb-3'
                                : ''
                            }
                          >
                            <p className="text-[11px] font-medium text-ink sm:text-xs">提示词</p>
                            <p className="mt-1.5 break-words whitespace-pre-wrap text-ink-soft">
                              {displayActive.prompt.trim()}
                            </p>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                      <AnimatePresence mode="popLayout">
                        {displayActive.details && displayActive.details.length > 0 ? (
                          <motion.div
                            key="details-section"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: preset.duration * 0.5, ease }}
                            className="space-y-1.5 border-b border-line/70 pb-3"
                          >
                            <p className="text-[11px] font-medium text-ink sm:text-xs">图片信息</p>
                            {displayActive.details.map((entry, idx) => (
                              <div key={`${idx}-${entry.label}`} className="space-y-0.5">
                                <p className="text-[10px] uppercase tracking-wide text-ink-soft/80">{entry.label}</p>
                                <p className="break-all text-ink-soft">{entry.value}</p>
                              </div>
                            ))}
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                      <AnimatePresence mode="popLayout">
                        {displayActive.referenceImageUrls && displayActive.referenceImageUrls.length > 0 ? (
                          <motion.div
                            key="refs-section"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: preset.duration * 0.5, ease, delay: preset.stagger * 0.5 }}
                            className={
                              displayActive.details && displayActive.details.length > 0
                                ? 'mt-3 border-b border-line/70 pb-3'
                                : ''
                            }
                          >
                            <p className="text-[11px] font-medium text-ink sm:text-xs">参考图</p>
                            <HorizontalChipStrip
                              className="mt-2 overscroll-x-contain pb-1"
                              trackClassName="justify-start gap-2"
                            >
                              {displayActive.referenceImageUrls.map((u, idx) => (
                                <ReferenceThumbButton
                                  key={`${idx}-${u.slice(0, 48)}`}
                                  url={u}
                                  index={idx}
                                  angular={angular}
                                  selected={compareRefIndex === idx}
                                  comparingOpen={compareRefIndex !== null}
                                  onSelectCompare={() =>
                                    setCompareRefIndex((prev) => (prev === idx ? null : idx))
                                  }
                                  bridge={{
                                    policy,
                                    openDesktop: ctx.openDesktop,
                                    openSheet: ctx.openSheet,
                                  }}
                                />
                              ))}
                            </HorizontalChipStrip>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </motion.aside>
                ) : hasAside && !isLgAside && !mobileDetailsOpen ? (
                  <motion.div
                    key="lightbox-expand-btn"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: panelDur * 0.8, ease }}
                    className="flex shrink-0 justify-center border-t border-line bg-surface-muted px-3 py-2"
                  >
                    <button
                      type="button"
                      className={`w-full max-w-sm border border-line bg-panel px-3 py-2 text-xs font-medium text-ink shadow-sm transition hover:bg-surface-muted sm:w-auto ${roundBtn}`}
                      aria-controls="image-lightbox-details"
                      onClick={() => setMobileDetailsOpen(true)}
                    >
                      展开详情
                    </button>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
            <ImageContextDesktopMenu
              angular={angular}
              open={ctx.desktop != null}
              anchor={
                ctx.desktop ? { x: ctx.desktop.x, y: ctx.desktop.y } : { x: 0, y: 0 }
              }
              items={menuItems}
              onClose={ctx.closeAll}
            />
            {!policy.useEmbeddedNativeImageMenus ? (
              <ImageContextMobileSheet
                angular={angular}
                open={ctx.sheet != null}
                items={menuItems}
                onClose={ctx.closeAll}
              />
            ) : null}
          </div>
        </div>
      ) : null,
    document.body,
  )
}
